// Registers the service worker built from src/service-worker.js.
//
// Adapted from cra-template-pwa, which this project was not scaffolded from
// (audit Finding 2.12a). Registration is opt-in: index.js calls register(),
// and a service worker only exists in a production build.
//
// Note on updates: the browser only looks for a new worker on a real page
// load, and an installed app brought back from the background makes none, so
// a deploy used to need two full closes to reach a phone. This file now calls
// registration.update() on every open and on every return to the foreground
// (at most once a minute), and hands a waiting worker to config.onUpdate -
// including one left waiting by an earlier open. index.js passes onUpdate to
// utils/swUpdate.js, and components/UpdateBanner.js offers the update.
//
// The new worker still waits: there is no skipWaiting on install and no
// forced reload. It takes over only when the user taps the banner, which
// posts SKIP_WAITING and reloads once on controllerchange, so a form in
// progress is never interrupted and a bad deploy cannot cause a reload loop.

// The foreground check runs at most this often.
const UPDATE_INTERVAL_MS = 60 * 1000;

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // A worker installed during an earlier open is already waiting, and
      // onupdatefound will not fire for it again.
      if (registration.waiting && navigator.serviceWorker.controller) {
        if (config && config.onUpdate) {
          config.onUpdate(registration);
        }
      }

      // Offline is normal in a truck, so a failed check is not an error.
      let lastCheck = Date.now();
      const checkForUpdate = () => {
        lastCheck = Date.now();
        registration.update().catch(() => {});
      };
      checkForUpdate();

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        if (Date.now() - lastCheck < UPDATE_INTERVAL_MS) return;
        checkForUpdate();
      });

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state !== 'installed') return;

          if (navigator.serviceWorker.controller) {
            // Previously cached content was updated. The new version is
            // waiting until the user taps the update banner.
            if (config && config.onUpdate) {
              config.onUpdate(registration);
            }
          } else {
            // First install: everything has been precached for offline use.
            if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Service worker registration failed:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // On localhost, make sure a service worker still exists on the server
  // before trusting one that a previous build left registered.
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      const missing =
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1);

      if (missing) {
        navigator.serviceWorker.ready.then((registration) =>
          registration.unregister().then(() => window.location.reload())
        );
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection. The app is running in offline mode.');
    });
}

export function register(config) {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    // The bundle is served from a different origin than the page, so a
    // service worker registered here would be out of scope.
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

export function unregister() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch((error) => console.error(error.message));
}
